import hubCategories, { defaultHubCategory } from '$lib/constants/hubCategories';
import type { ArticleData, IncludedObject, IncludedTerm } from '$lib/types/articles';
import type { BaseLink } from '$lib/types/base';

// Add separate args for articleData and include since article lists responses
// share a single included object
export const getAllTermsForArticle = (
  articleData: ArticleData,
  includedData: IncludedObject[]
): IncludedTerm[] => {
  const relationshipIds = Object.values(articleData.relationships).flatMap((relationship) => {
    if (Array.isArray(relationship.data)) {
      return relationship.data.map((item: any) => item.id);
    } else if (relationship.data) {
      return relationship.data.id;
    }
    return [];
  });

  return includedData.filter(
    (includedObject): includedObject is IncludedTerm =>
      includedObject.type.includes('taxonomy_term') && relationshipIds.includes(includedObject.id)
  );
};

export const sortTermsByWeight = (terms: IncludedTerm[]) => {
  return terms.sort((a, b) => a.attributes.weight - b.attributes.weight);
};

export const isHubTerm = (term: IncludedTerm) => {
  const slug = term.attributes.path.alias.replace('/', '');
  return Object.hasOwn(hubCategories, slug);
};

export const getTermNamesArrayForTerms = (terms: IncludedTerm[]) => {
  return terms.map((term) => term.attributes.name);
};

export const getHubTermForArticle = (articleTerms: IncludedTerm[]): IncludedTerm => {
  const hubTerms = articleTerms.filter((term) => isHubTerm(term));
  const sortedHubTerms = sortTermsByWeight(hubTerms);

  // return iPhone as default hub if not hub found
  if (sortedHubTerms.length > 0) {
    return sortedHubTerms[0];
  } else {
    return defaultHubCategory;
  }
};

// Article term trail (breadcrumbs) is in the format:
// Home > Hub Crumb > Tag Crumb
//
// To determine what vocabulary to use for each crumb:
// 1. If an Article Type hub term –
//    Home > [Article Type Hub Term] > [Top Device Type Term]
// 2. If there's no Article Type hub term –
//    Home > [Device Type Hub Term] > [Top Life Activity or Feature term]
//
//"Top" is defined as the lowest-weighted term. As such, admins must sort
// term vocabularies in desired ranking.
export const getTermTrailForArticle = (
  articleData: ArticleData,
  includedData: IncludedObject[]
): BaseLink[] => {
  const allTerms = getAllTermsForArticle(articleData, includedData);

  const hubTerm = getHubTermForArticle(allTerms);

  const nonHubDeviceTerms = allTerms.filter(
    (term) => term.type.includes('device_or_accessory_type') && !isHubTerm(term)
  );

  const nonHubFeatureTerms = allTerms.filter(
    (term) => term.type.includes('feature_name_service_category') && !isHubTerm(term)
  );

  const nonHubActivityTerms = allTerms.filter(
    (term) => term.type.includes('life_activity_or_topic') && !isHubTerm(term)
  );

  const topDeviceTerm = sortTermsByWeight(nonHubDeviceTerms)[0];
  const topFeatureTerm = sortTermsByWeight(nonHubFeatureTerms)[0];
  const topActivityTerm = sortTermsByWeight(nonHubActivityTerms)[0];

  let tagTerm: IncludedTerm;

  if (hubTerm.type.includes('device_or_accessory_type')) {
    tagTerm = topActivityTerm;
  } else {
    if (topDeviceTerm) {
      tagTerm = topDeviceTerm;
    } else if (topFeatureTerm) {
      tagTerm = topFeatureTerm;
    } else {
      tagTerm = topActivityTerm;
    }
  }

  return [
    {
      text: hubTerm.attributes.name,
      href: hubTerm.attributes.path.alias
    },
    ...(tagTerm
      ? [
          {
            text: tagTerm.attributes.name,
            href: tagTerm.attributes.path.alias
          }
        ]
      : [])
  ];
};

export const getBaseLinkForTerm = (term: IncludedTerm): BaseLink | undefined => {
  if (term.attributes) {
    return {
      text: term.attributes.name,
      href: term.attributes.path.alias
    };
  }
};
